import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';

// --- ANIMATION VARIANTS ---
const pageVariants = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } },
  exit: { opacity: 0, y: -20, transition: { duration: 0.2 } }
};

const containerVariants = {
  hidden: { opacity: 0 },
  show: { opacity: 1, transition: { staggerChildren: 0.1 } }
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  show: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 24 } }
};

export default function AiRiskGrader() {
  const navigate = useNavigate();
  
  // App State: 'landing' | 'assessment' | 'lead_capture' | 'report'
  const [step, setStep] = useState('landing');
  const [loading, setLoading] = useState(false);
  
  // Form Data States
  const [toolName, setToolName] = useState('');
  const [answers, setAnswers] = useState({
    data_type: '',
    approval: '',
    training: ''
  });
  const [leadInfo, setLeadInfo] = useState({
    full_name: '',
    contact_email: '',
    org_name: ''
  });
  
  // Results State
  const [results, setResults] = useState(null);

  // --- REHYDRATE STATE FROM SESSION STORAGE ---
  useEffect(() => {
    const savedState = sessionStorage.getItem('fcaio_risk_grader_state');
    if (savedState) {
      try {
        const parsed = JSON.parse(savedState);
        if (parsed.step) setStep(parsed.step);
        if (parsed.toolName) setToolName(parsed.toolName);
        if (parsed.answers) setAnswers(parsed.answers);
        if (parsed.leadInfo) setLeadInfo(parsed.leadInfo);
        if (parsed.results) setResults(parsed.results);
      } catch (e) {
        console.error("Failed to parse saved grader state");
      }
    }
  }, []);

  // --- SAVE STATE TO SESSION STORAGE ON CHANGE ---
  useEffect(() => {
    if (step !== 'landing') {
      sessionStorage.setItem('fcaio_risk_grader_state', JSON.stringify({
        step, toolName, answers, leadInfo, results
      }));
    }
  }, [step, toolName, answers, leadInfo, results]);

  // --- NEW: CLOSE REPORT FUNCTION ---
  const handleCloseReport = () => {
    // Clear the memory so it starts fresh
    sessionStorage.removeItem('fcaio_risk_grader_state');
    
    // Reset all states
    setToolName('');
    setAnswers({ data_type: '', approval: '', training: '' });
    setLeadInfo({ full_name: '', contact_email: '', org_name: '' });
    setResults(null);
    
    // Send back to the landing view
    setStep('landing');
  };

  // --- LOGIC: Calculate the Risk Score ---
  const calculateRisk = () => {
    let score = 0;
    
    if (answers.data_type === 'Confidential/PII') score += 50;
    if (answers.data_type === 'Internal Business Data') score += 25;
    if (answers.data_type === 'Public Data') score += 0;

    if (answers.approval === 'Unsanctioned (Shadow AI)') score += 40;
    if (answers.approval === 'Unsure') score += 20;
    if (answers.approval === 'Officially Sanctioned') score += 0;

    if (answers.training === 'Yes, it trains on our data') score += 40;
    if (answers.training === 'Unsure') score += 20;
    if (answers.training === 'No, strict opt-out') score += 0;

    const finalScore = Math.min(score, 100);
    
    let grade = 'A';
    let color = 'text-emerald-500';
    let bg = 'bg-emerald-50';
    
    if (finalScore > 30) { grade = 'C'; color = 'text-amber-500'; bg = 'bg-amber-50'; }
    if (finalScore > 65) { grade = 'F'; color = 'text-red-500'; bg = 'bg-red-50'; }

    return { score: finalScore, grade, color, bg };
  };

  const handleAssessmentSubmit = (e) => {
    e.preventDefault();
    setStep('lead_capture');
  };

  const handleLeadSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    const calculated = calculateRisk();
    setResults(calculated);

    try {
      const baseURL = process.env.REACT_APP_API_URL || 'http://localhost:5000';
      await axios.post(`${baseURL}/api/leads/grader-submit`, {
        full_name: leadInfo.full_name,
        contact_email: leadInfo.contact_email,
        org_name: leadInfo.org_name,
        tool_name: toolName,
        risk_score: calculated.score,
        risk_grade: calculated.grade,
        answers: answers
      });
      setStep('report');
    } catch (error) {
      alert("Something went wrong generating your report. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // --- VIEWS (WRAPPED IN ANIMATE PRESENCE) ---
  return (
    <AnimatePresence mode="wait">
      {step === 'landing' && (
        <motion.div key="landing" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen bg-slate-900 flex items-center justify-center p-6 font-sans">
          <div className="max-w-3xl w-full bg-white rounded-3xl shadow-2xl overflow-hidden flex flex-col md:flex-row">
            <div className="p-10 md:w-3/5">
              <div className="w-12 h-12 bg-blue-100 text-blue-600 rounded-xl flex items-center justify-center text-2xl mb-6">🛡️</div>
              <h1 className="text-4xl font-extrabold text-slate-900 mb-4 tracking-tight">Free AI Risk Grader</h1>
              <p className="text-slate-600 text-lg mb-8">Shadow AI is exposing enterprise data daily. Enter any AI tool your team uses, and get an instant security grade and compliance report.</p>
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} onClick={() => setStep('assessment')} className="bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 px-8 rounded-lg transition shadow-lg w-full md:w-auto">
                Grade My AI Tool Now ➔
              </motion.button>
            </div>
            <div className="bg-slate-50 md:w-2/5 p-10 border-l border-slate-100 flex items-center justify-center">
               <div className="text-center">
                 <div className="text-7xl mb-4">A-F</div>
                 <p className="text-sm font-bold text-slate-500 uppercase tracking-widest">Instant Grading</p>
               </div>
            </div>
          </div>
        </motion.div>
      )}

      {step === 'assessment' && (
        <motion.div key="assessment" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen bg-slate-50 py-12 px-4 font-sans">
          <div className="max-w-2xl mx-auto bg-white p-8 rounded-2xl shadow-sm border border-slate-200 relative">
            
            <button onClick={handleCloseReport} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1 z-10" aria-label="Cancel">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="mb-8 pr-8">
              <p className="text-xs font-bold text-blue-600 uppercase tracking-wider mb-2">Step 1 of 2</p>
              <h2 className="text-2xl font-bold text-slate-900">Tool Assessment</h2>
            </div>
            <motion.form variants={containerVariants} initial="hidden" animate="show" onSubmit={handleAssessmentSubmit} className="space-y-8">
              
              <motion.div variants={itemVariants}>
                <label className="block text-sm font-bold text-slate-700 mb-2">1. What is the name of the AI Tool?</label>
                <input required type="text" placeholder="e.g., ChatGPT, Midjourney, Jasper" className="w-full border border-slate-300 p-3 rounded-lg focus:ring-2 focus:ring-blue-500" value={toolName} onChange={e => setToolName(e.target.value)} />
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-bold text-slate-700 mb-2">2. What type of data is processed in this tool?</label>
                <select required className="w-full border border-slate-300 p-3 rounded-lg bg-white" value={answers.data_type} onChange={e => setAnswers({...answers, data_type: e.target.value})}>
                  <option value="">-- Select Data Type --</option>
                  <option value="Public Data">Public Data (Marketing copy, general web research)</option>
                  <option value="Internal Business Data">Internal Business Data (Meeting notes, internal docs)</option>
                  <option value="Confidential/PII">Confidential / PII (Customer info, source code, financials)</option>
                </select>
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-bold text-slate-700 mb-2">3. Is this tool officially sanctioned by IT/Security?</label>
                <div className="space-y-2">
                  {['Officially Sanctioned', 'Unsanctioned (Shadow AI)', 'Unsure'].map(opt => (
                    <label key={opt} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input required type="radio" name="approval" value={opt} onChange={e => setAnswers({...answers, approval: e.target.value})} className="text-blue-600 w-4 h-4" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants}>
                <label className="block text-sm font-bold text-slate-700 mb-2">4. Does the vendor train their models on your inputs?</label>
                <div className="space-y-2">
                  {['Yes, it trains on our data', 'No, strict opt-out', 'Unsure'].map(opt => (
                    <label key={opt} className="flex items-center gap-3 p-3 border rounded-lg cursor-pointer hover:bg-slate-50 transition-colors">
                      <input required type="radio" name="training" value={opt} onChange={e => setAnswers({...answers, training: e.target.value})} className="text-blue-600 w-4 h-4" />
                      <span className="text-sm text-slate-700">{opt}</span>
                    </label>
                  ))}
                </div>
              </motion.div>

              <motion.div variants={itemVariants} className="pt-4 border-t border-slate-200">
                <motion.button whileHover={{ scale: 1.01 }} whileTap={{ scale: 0.99 }} type="submit" className="w-full bg-slate-900 hover:bg-slate-800 text-white font-bold py-3.5 rounded-lg transition shadow-md">
                  Analyze Risk Score
                </motion.button>
              </motion.div>
            </motion.form>
          </div>
        </motion.div>
      )}

      {step === 'lead_capture' && (
        <motion.div key="lead_capture" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen bg-slate-900 py-12 px-4 flex items-center justify-center font-sans">
          <div className="max-w-md w-full bg-white p-8 rounded-2xl shadow-2xl relative overflow-hidden">
            
            <button onClick={() => setStep('assessment')} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600 transition-colors p-1 z-10" aria-label="Go back">
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>

            <div className="absolute top-0 left-0 w-full h-1.5 bg-blue-600 animate-pulse"></div>
            <div className="text-center mb-8 mt-4">
              <motion.div animate={{ rotate: 360 }} transition={{ duration: 3, repeat: Infinity, ease: "linear" }} className="w-16 h-16 bg-blue-100 text-blue-600 rounded-full flex items-center justify-center text-3xl mx-auto mb-4">⚙️</motion.div>
              <h2 className="text-2xl font-bold text-slate-900">Your Report is Ready</h2>
              <p className="text-slate-500 text-sm mt-2">Enter your details below to instantly unlock your AI Risk Grade and mitigation strategies.</p>
            </div>

            <form onSubmit={handleLeadSubmit} className="space-y-4">
              <input required type="text" placeholder="Full Name" className="w-full border border-slate-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={leadInfo.full_name} onChange={e => setLeadInfo({...leadInfo, full_name: e.target.value})} />
              <input required type="email" placeholder="Work Email" className="w-full border border-slate-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={leadInfo.contact_email} onChange={e => setLeadInfo({...leadInfo, contact_email: e.target.value})} />
              <input required type="text" placeholder="Organization" className="w-full border border-slate-300 p-3 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 outline-none" value={leadInfo.org_name} onChange={e => setLeadInfo({...leadInfo, org_name: e.target.value})} />
              
              <motion.button whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }} type="submit" disabled={loading} className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 rounded-lg transition shadow-md mt-4 disabled:opacity-50">
                {loading ? 'Generating...' : 'Unlock My Risk Report'}
              </motion.button>
            </form>
          </div>
        </motion.div>
      )}

      {step === 'report' && results && (
        <motion.div key="report" variants={pageVariants} initial="initial" animate="animate" exit="exit" className="min-h-screen bg-slate-50 py-12 px-4 font-sans">
          <div className="max-w-4xl mx-auto">
            <div className="bg-white p-8 md:p-12 rounded-3xl shadow-lg border border-slate-200 text-center mb-8 relative overflow-hidden">
              
              <div className={`absolute top-0 left-0 w-full h-3 ${results.bg.replace('bg-', 'bg-').replace('50', '500')}`}></div>
              
              <button 
                onClick={handleCloseReport} 
                className="absolute top-6 right-6 bg-slate-100 hover:bg-slate-200 text-slate-500 hover:text-slate-800 transition-colors p-2 rounded-full z-10" 
                aria-label="Close Report"
                title="Start Over"
              >
                <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
              
              <h2 className="text-slate-500 font-bold uppercase tracking-widest text-sm mb-2 mt-4">Risk Assessment For</h2>
              <h1 className="text-4xl font-extrabold text-slate-900 mb-8">{toolName}</h1>

              <div className="flex flex-col items-center justify-center mb-8">
                <motion.div initial={{ scale: 0 }} animate={{ scale: 1 }} transition={{ type: 'spring', bounce: 0.5 }} className={`w-40 h-40 rounded-full ${results.bg} border-8 border-white shadow-xl flex items-center justify-center mb-4`}>
                  <span className={`text-7xl font-black ${results.color}`}>{results.grade}</span>
                </motion.div>
                <p className="text-xl font-bold text-slate-700">Risk Score: {results.score} / 100</p>
                <p className="text-sm text-slate-500 mt-2">A copy of this report has been securely emailed to {leadInfo.contact_email}.</p>
              </div>

              <div className="bg-slate-50 p-8 rounded-2xl text-left border border-slate-200 shadow-inner">
                <h3 className="font-bold text-slate-900 mb-6 text-xl border-b border-slate-200 pb-3">Vulnerability Breakdown</h3>
                <motion.ul variants={containerVariants} initial="hidden" animate="show" className="space-y-5 text-sm text-slate-700">
                  
                  {answers.data_type.includes('PII') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">🚨</span> 
                      <div><strong className="text-slate-900 block text-base">Critical Data Exposure</strong>Processing Sensitive/PII data in third-party AI tools without explicit enterprise agreements violates major compliance frameworks (GDPR, HIPAA, SOC2).</div>
                    </motion.li>
                  )}
                  {answers.data_type.includes('Internal') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">⚠️</span> 
                      <div><strong className="text-slate-900 block text-base">Internal Data Risk</strong>Inputting internal business documents and meeting notes into external tools creates a medium-level data leakage vector.</div>
                    </motion.li>
                  )}
                  
                  {answers.approval.includes('Shadow') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">⚠️</span> 
                      <div><strong className="text-slate-900 block text-base">Shadow AI Operations</strong>Unsanctioned usage bypasses enterprise security monitoring, making it impossible to audit prompts, track data leakage, or revoke access upon offboarding.</div>
                    </motion.li>
                  )}
                  {answers.approval.includes('Unsure') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">🔍</span> 
                      <div><strong className="text-slate-900 block text-base">Approval Visibility Gap</strong>Uncertainty around IT approval indicates a lack of clear AI governance policies communicated to the wider team.</div>
                    </motion.li>
                  )}

                  {answers.training.includes('Yes') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">🔓</span> 
                      <div><strong className="text-slate-900 block text-base">Proprietary Data Leakage</strong>Vendor model training is enabled. This means your intellectual property, internal strategies, or meeting notes may be regurgitated to your competitors.</div>
                    </motion.li>
                  )}
                  {answers.training.includes('Unsure') && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">❓</span> 
                      <div><strong className="text-slate-900 block text-base">Unknown Training Policy</strong>Failing to verify the vendor's data training policy leaves the organization exposed to unintentional intellectual property surrender.</div>
                    </motion.li>
                  )}

                  {results.score <= 30 && (
                    <motion.li variants={itemVariants} className="flex gap-4 items-start">
                      <span className="text-2xl mt-0.5">✅</span> 
                      <div><strong className="text-slate-900 block text-base">Acceptable Parameters</strong>This tool is currently operating within generally safe enterprise parameters. Continue to monitor for unannounced Terms of Service changes.</div>
                    </motion.li>
                  )}
                </motion.ul>
              </div>
            </div>

            {/* --- 4-TIER UPSELL PIPELINE --- */}
            <div className="bg-slate-900 rounded-3xl p-8 md:p-12 text-white shadow-xl">
              <div className="text-center mb-10">
                <h2 className="text-3xl font-bold mb-3">Stop Guessing. Start Governing.</h2>
                <p className="text-slate-400 max-w-2xl mx-auto">This was just one tool. The average enterprise has 15+ undocumented AI tools running right now. Choose your path to secure AI adoption.</p>
              </div>
              
              <motion.div variants={containerVariants} initial="hidden" animate="show" className="grid grid-cols-1 md:grid-cols-2 gap-4">
                
                <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} className="bg-slate-800 p-6 rounded-xl border border-slate-700 hover:border-blue-500 transition-colors flex flex-col h-full">
                  <h3 className="text-lg font-bold text-white mb-2">⏱️ Free Trial</h3>
                  <p className="text-slate-400 text-sm mb-6 flex-1">Kick the tires. Access the base FCAIO platform to manually inventory your top 3 tools and run basic risk analysis for 7 days.</p>
                  <button onClick={() => navigate('/register', { state: { from: '/risk-grader', targetTier: 0, fromButton: 'Start Free Trial' }})} className="w-full bg-slate-700 hover:bg-slate-600 text-white font-bold py-2.5 rounded-lg text-sm transition">Start Free Trial</button>
                </motion.div>

                <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} className="bg-slate-800 p-6 rounded-xl border border-slate-700 hover:border-emerald-500 transition-colors flex flex-col h-full">
                  <h3 className="text-lg font-bold text-white mb-2">🛡️ Tier 1: Platform Access</h3>
                  <p className="text-slate-400 text-sm mb-6 flex-1">Continuous governance. 20 AI inventory tracking, 20 risk assessments, and 20 executive board reporting.</p>
                  <button onClick={() => navigate('/register', { state: { from: '/risk-grader', targetTier: 1, fromButton: 'Subscribe to Tier 1' }})} className="w-full bg-emerald-600 hover:bg-emerald-500 text-white font-bold py-2.5 rounded-lg text-sm transition">Subscribe to Tier 1</button>
                </motion.div>

                <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} className="bg-slate-800 p-6 rounded-xl border border-slate-700 hover:border-purple-500 transition-colors flex flex-col h-full">
                  <h3 className="text-lg font-bold text-white mb-2">🚀 Tier 2: 5-Day Sprint</h3>
                  <p className="text-slate-400 text-sm mb-6 flex-1">Accelerated deployment. We analyze your systems, establish your AI policy, and train your team over an intensive 5-day guided sprint.</p>
                  <button onClick={() => navigate('/register', { state: { from: '/risk-grader', targetTier: 2, fromButton: 'Request Tier 2 Sprint' }})} className="w-full bg-purple-600 hover:bg-purple-500 text-white font-bold py-2.5 rounded-lg text-sm transition">Request Tier 2 Sprint</button>
                </motion.div>

                <motion.div variants={itemVariants} whileHover={{ scale: 1.02 }} className="bg-slate-800 p-6 rounded-xl border border-slate-700 hover:border-amber-500 transition-colors flex flex-col h-full">
                  <h3 className="text-lg font-bold text-white mb-2">💼 Tier 3: Retainer</h3>
                  <p className="text-slate-400 text-sm mb-6 flex-1">Your dedicated Fractional CAIO. Comprehensive enterprise strategy, vendor negotiations, and ongoing executive advisory.</p>
                  <button onClick={() => navigate('/book-discovery', { state: { from: '/risk-grader' }})} className="w-full bg-transparent border border-amber-500 text-amber-500 hover:bg-amber-500 hover:text-slate-900 font-bold py-2.5 rounded-lg text-sm transition">Book Discovery Call</button>
                </motion.div>

              </motion.div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}